/**
 * HelpGuidePage - In-app help documentation for contractors
 * Protected behind authentication - only logged-in users can access
 * Personalized with company name and industry-specific tips
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen,
  Search,
  PlayCircle,
  FileText,
  Camera,
  Calendar,
  DollarSign,
  Users,
  CheckCircle,
  Smartphone,
  Wifi,
  WifiOff,
  ChevronRight,
  HelpCircle,
  MessageSquare,
  Hammer,
  TreePine,
  Home,
  Wrench,
  Paintbrush,
  Droplets,
  Zap,
  Wind,
  Building2,
} from 'lucide-react';

// Industry-specific tips and examples
const industryContent = {
  concrete: {
    name: 'Concrete',
    icon: Building2,
    color: 'bg-gray-100 text-gray-700',
    photoTips: [
      { type: 'Before', desc: 'Capture existing cracks, settling, damaged areas' },
      { type: 'During', desc: 'Document rebar placement, forms, pour process' },
      { type: 'After', desc: 'Show finished surface, edges, cleanup from multiple angles' },
    ],
    measurementTips: [
      'Driveways & Patios: Measure in square feet',
      'Sidewalks: Square feet or linear feet',
      'Steps: Count per step + linear feet for width',
      'Curbs & Borders: Linear feet',
      'Always note thickness: 4", 6", 8"',
      'Add 5-10% for waste',
    ],
    commonLineItems: [
      'Demolition & Removal (per sq ft)',
      'Excavation & Grading (per sq ft)',
      'Gravel Base 4" (per sq ft)',
      'Forming (per linear ft)',
      'Rebar/Wire Mesh (per sq ft)',
      'Concrete Pour 4" 3500 PSI (per sq ft)',
      'Broom Finish (per sq ft)',
      'Stamp Pattern (per sq ft)',
      'Sealer (per sq ft)',
      'Control Joints (per linear ft)',
      'Cleanup & Haul Away (flat fee)',
    ],
  },
  landscaping: {
    name: 'Landscaping',
    icon: TreePine,
    color: 'bg-green-100 text-green-700',
    photoTips: [
      { type: 'Before', desc: 'Capture overgrowth, dead plants, problem areas' },
      { type: 'During', desc: 'Document progress, plant placement, hardscape work' },
      { type: 'After', desc: 'Show finished beds, clean edges, overall transformation' },
    ],
    measurementTips: [
      'Lawns: Square footage for sod/seed',
      'Beds: Square footage for mulch (depth matters!)',
      'Edging: Linear feet',
      'Plants: Count by type and size',
      'Mulch: Calculate cubic yards (sq ft × depth ÷ 324)',
      'Note sun/shade conditions for plant selection',
    ],
    commonLineItems: [
      'Lawn Mowing (per visit or monthly)',
      'Edging & Trimming (per visit)',
      'Mulch Installation (per cubic yard)',
      'Plant Installation (per plant by size)',
      'Bed Preparation (per sq ft)',
      'Sod Installation (per sq ft)',
      'Tree/Shrub Trimming (per hour or per plant)',
      'Leaf Removal (per visit or by bag)',
      'Seasonal Cleanup (flat fee)',
      'Irrigation Repair (hourly + parts)',
    ],
  },
  roofing: {
    name: 'Roofing',
    icon: Home,
    color: 'bg-red-100 text-red-700',
    photoTips: [
      { type: 'Before', desc: 'Document damage, missing shingles, leaks, flashing issues' },
      { type: 'During', desc: 'Show tear-off, decking condition, underlayment, install progress' },
      { type: 'After', desc: 'Finished roof from multiple angles, clean gutters, ground cleanup' },
    ],
    measurementTips: [
      'Roof area: Calculate in "squares" (1 square = 100 sq ft)',
      'Pitch matters: Steeper = more material + labor',
      'Count layers for tear-off pricing',
      'Measure ridge, hips, valleys in linear feet',
      'Note chimney, skylight, vent locations',
      'Check decking condition during inspection',
    ],
    commonLineItems: [
      'Tear-Off (per square, add for layers)',
      'Decking Repair (per sheet)',
      'Underlayment (per square)',
      'Shingle Installation (per square)',
      'Ridge Cap (per linear ft)',
      'Flashing (per linear ft or each)',
      'Drip Edge (per linear ft)',
      'Vent Installation (each)',
      'Chimney Flashing (each)',
      'Gutter Cleaning (per linear ft)',
      'Dumpster/Disposal (flat fee)',
    ],
  },
  plumbing: {
    name: 'Plumbing',
    icon: Droplets,
    color: 'bg-blue-100 text-blue-700',
    photoTips: [
      { type: 'Before', desc: 'Document leaks, corrosion, damage, existing fixtures' },
      { type: 'During', desc: 'Show pipe routing, connections, access points' },
      { type: 'After', desc: 'Finished installation, test results, cleanup' },
    ],
    measurementTips: [
      'Pipe runs: Linear feet by diameter',
      'Fixtures: Count by type',
      'Water heaters: Note gallon capacity',
      'Drain cleaning: Note length and access difficulty',
      'Note material types (copper, PEX, PVC)',
    ],
    commonLineItems: [
      'Service Call / Diagnostic (flat fee)',
      'Pipe Repair (per repair or hourly)',
      'Fixture Installation (each)',
      'Water Heater Install (each + disposal)',
      'Drain Cleaning (flat fee by type)',
      'Toilet Repair/Replace (each)',
      'Faucet Install (each)',
      'Garbage Disposal (each)',
      'Shut-off Valve (each)',
      'Hourly Labor (per hour)',
    ],
  },
  electrical: {
    name: 'Electrical',
    icon: Zap,
    color: 'bg-yellow-100 text-yellow-700',
    photoTips: [
      { type: 'Before', desc: 'Document panel condition, old wiring, safety issues' },
      { type: 'During', desc: 'Show wire routing, connections, code compliance' },
      { type: 'After', desc: 'Finished install, panel labels, working fixtures' },
    ],
    measurementTips: [
      'Circuits: Count new vs existing',
      'Wire runs: Estimate linear feet',
      'Outlets/Switches: Count by type',
      'Panel capacity: Note amperage',
      'Note if attic/crawl access available',
    ],
    commonLineItems: [
      'Service Call / Diagnostic (flat fee)',
      'Outlet Installation (each)',
      'Switch Installation (each)',
      'Light Fixture Install (each)',
      'Ceiling Fan Install (each)',
      'Circuit Addition (each)',
      'Panel Upgrade (each)',
      'GFCI Outlet (each)',
      'Smoke Detector (each)',
      'Hourly Labor (per hour)',
    ],
  },
  hvac: {
    name: 'HVAC',
    icon: Wind,
    color: 'bg-cyan-100 text-cyan-700',
    photoTips: [
      { type: 'Before', desc: 'Document unit condition, age, model numbers' },
      { type: 'During', desc: 'Show installation, ductwork, connections' },
      { type: 'After', desc: 'Running system, thermostat, cleanup' },
    ],
    measurementTips: [
      'Square footage: For load calculations',
      'Unit size: Tonnage for AC, BTU for furnace',
      'Ductwork: Linear feet if replacing',
      'Note unit location and access',
      'Check existing electrical capacity',
    ],
    commonLineItems: [
      'Service Call / Diagnostic (flat fee)',
      'AC Tune-Up (flat fee)',
      'Furnace Tune-Up (flat fee)',
      'Filter Replacement (each)',
      'Refrigerant Recharge (per lb)',
      'Thermostat Install (each)',
      'AC Unit Replacement (each)',
      'Furnace Replacement (each)',
      'Ductwork Repair (per section)',
      'Duct Cleaning (per vent or flat)',
    ],
  },
  painting: {
    name: 'Painting',
    icon: Paintbrush,
    color: 'bg-purple-100 text-purple-700',
    photoTips: [
      { type: 'Before', desc: 'Document peeling, cracks, color to cover, prep needed' },
      { type: 'During', desc: 'Show prep work, primer, first coat progress' },
      { type: 'After', desc: 'Finished walls, trim, clean lines, touch-ups complete' },
    ],
    measurementTips: [
      'Walls: Square footage (height × width)',
      'Ceilings: Square footage',
      'Trim/Doors: Linear feet or count',
      'Exterior: Include prep time for condition',
      'Note number of coats needed',
      'Calculate paint quantity: ~350 sq ft per gallon',
    ],
    commonLineItems: [
      'Interior Wall Paint (per sq ft or room)',
      'Ceiling Paint (per sq ft)',
      'Trim Paint (per linear ft)',
      'Door Paint (each, per side)',
      'Cabinet Paint (per linear ft or door)',
      'Exterior Paint (per sq ft)',
      'Prep Work / Patching (hourly or per area)',
      'Primer (per sq ft)',
      'Pressure Washing (per sq ft)',
      'Paint & Materials (itemized)',
    ],
  },
  general: {
    name: 'General Contracting',
    icon: Hammer,
    color: 'bg-orange-100 text-orange-700',
    photoTips: [
      { type: 'Before', desc: 'Document existing conditions, areas of work' },
      { type: 'During', desc: 'Show progress at each phase' },
      { type: 'After', desc: 'Completed work from multiple angles' },
    ],
    measurementTips: [
      'Room dimensions for flooring, paint, etc.',
      'Linear feet for trim, baseboards, etc.',
      'Square footage for surfaces',
      'Count fixtures, outlets, etc.',
      'Note access and demo requirements',
    ],
    commonLineItems: [
      'Labor (per hour or per day)',
      'Materials (itemized)',
      'Demolition (per sq ft or flat)',
      'Disposal/Dumpster (flat fee)',
      'Permits (pass-through)',
      'Subcontractor Work (itemized)',
    ],
  },
};

// Map service types to industry content
const serviceToIndustry = {
  'concrete': 'concrete',
  'flatwork': 'concrete',
  'stamped_concrete': 'concrete',
  'landscaping': 'landscaping',
  'lawn_care': 'landscaping',
  'tree_service': 'landscaping',
  'irrigation': 'landscaping',
  'roofing': 'roofing',
  'gutters': 'roofing',
  'siding': 'roofing',
  'plumbing': 'plumbing',
  'electrical': 'electrical',
  'hvac': 'hvac',
  'heating': 'hvac',
  'cooling': 'hvac',
  'painting': 'painting',
  'drywall': 'painting',
  'construction': 'general',
  'remodeling': 'general',
  'handyman': 'general',
  'carpentry': 'general',
};

const HelpGuidePage = () => {
  const { user, organization, isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');
  const [onboardingStats, setOnboardingStats] = useState({
    hasCompanyProfile: false,
    hasPaymentMethods: false,
    hasCustomers: false,
    hasSiteVisits: false,
    hasEstimates: false,
  });
  const navigate = useNavigate();

  // Fetch onboarding completion status
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!organization?.id) return;
      
      try {
        // Check company profile - has name, phone, or address set
        const hasProfile = !!(organization.name && organization.name !== 'Demo Contractor Co.' && 
          (organization.phone || organization.email || organization.address));
        
        // Check payment methods - any payment method enabled
        const paymentSettings = organization.payment_settings || {};
        const hasPayments = !!(paymentSettings.accept_card_online || paymentSettings.accept_cash || 
          paymentSettings.accept_check || paymentSettings.accept_venmo || 
          paymentSettings.accept_cashapp || paymentSettings.accept_paypal || paymentSettings.accept_zelle);
        
        // Fetch counts from API
        const [clientsRes, siteVisitsRes, estimatesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/clients`).then(r => r.json()).catch(() => []),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/site-visits`).then(r => r.json()).catch(() => []),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/estimates`).then(r => r.json()).catch(() => []),
        ]);
        
        setOnboardingStats({
          hasCompanyProfile: hasProfile,
          hasPaymentMethods: hasPayments,
          hasCustomers: Array.isArray(clientsRes) && clientsRes.length > 0,
          hasSiteVisits: Array.isArray(siteVisitsRes) && siteVisitsRes.length > 0,
          hasEstimates: Array.isArray(estimatesRes) && estimatesRes.length > 0,
        });
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };
    
    checkOnboardingStatus();
  }, [organization?.id]);

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Get company name and industry
  const companyName = organization?.company_name || organization?.name || 'Your Company';
  const services = organization?.service_types_enabled || [];
  
  // Determine primary industry based on services or business_type
  const getPrimaryIndustry = () => {
    // First check business_type directly
    const bizType = organization?.business_type?.toLowerCase();
    if (bizType && serviceToIndustry[bizType]) {
      return serviceToIndustry[bizType];
    }
    if (bizType && industryContent[bizType]) {
      return bizType;
    }
    // Then check enabled services
    for (const service of services) {
      const industry = serviceToIndustry[service.toLowerCase()];
      if (industry && industryContent[industry]) {
        return industry;
      }
    }
    return 'general';
  };
  
  const primaryIndustry = getPrimaryIndustry();
  const industryData = industryContent[primaryIndustry] || industryContent.general;

  const sections = [
    { id: 'getting-started', label: 'Getting Started', icon: PlayCircle },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'site-visits', label: 'Site Visits', icon: Calendar },
    { id: 'field-notes', label: 'Field Notes', icon: Camera },
    { id: 'estimates', label: 'Estimates', icon: FileText },
    { id: 'jobs', label: 'Jobs & Calendar', icon: Calendar },
    { id: 'invoices', label: 'Invoices', icon: DollarSign },
    { id: 'payments', label: 'Payment Setup', icon: DollarSign },
    { id: 'industry-tips', label: `${industryData.name} Tips`, icon: industryData.icon },
    { id: 'mobile', label: 'Mobile Tips', icon: Smartphone },
    { id: 'offline', label: 'Offline Mode', icon: WifiOff },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Personalized */}
      <div className="bg-primary text-white py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8" />
            <h1 className="font-heading text-3xl font-bold">{companyName} Help Guide</h1>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <Badge className={industryData.color}>{industryData.name}</Badge>
            <span className="text-white/70 text-sm">Customized tips for your industry</span>
          </div>
          <p className="text-white/80 text-lg">Everything you need to get the most out of ContractorOS</p>
          
          {/* Search */}
          <div className="mt-6 relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white text-gray-900 h-12"
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="grid md:grid-cols-[250px_1fr] gap-8">
          {/* Sidebar Navigation */}
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeSection === section.id
                    ? 'bg-primary text-white'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <section.icon className="w-5 h-5" />
                <span className="font-medium">{section.label}</span>
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
            {/* Getting Started */}
            {activeSection === 'getting-started' && (
              <div className="space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-bold mb-4">Getting Started</h2>
                  <p className="text-muted-foreground mb-6">
                    Welcome! Follow these steps to set up your account and start managing your jobs.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-gray-400" />
                    Quick Start Checklist
                  </h3>
                  
                  <div className="space-y-2">
                    {[
                      { step: '1', title: 'Set up your company profile', link: '/settings', completed: onboardingStats.hasCompanyProfile },
                      { step: '2', title: 'Add your payment methods', link: '/settings', completed: onboardingStats.hasPaymentMethods, hash: '#payments' },
                      { step: '3', title: 'Add your first customer', link: '/clients', completed: onboardingStats.hasCustomers },
                      { step: '4', title: 'Schedule your first site visit', link: '/site-visits', completed: onboardingStats.hasSiteVisits },
                      { step: '5', title: 'Create your first estimate', link: '/estimates', completed: onboardingStats.hasEstimates },
                    ].map((item) => (
                      <button
                        key={item.step}
                        onClick={() => navigate(item.link)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left border ${
                          item.completed 
                            ? 'bg-white border-gray-200 opacity-60' 
                            : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                      >
                        {item.completed ? (
                          <span className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
                            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        ) : (
                          <span className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs font-medium text-gray-500 flex-shrink-0">
                            {item.step}
                          </span>
                        )}
                        <span className={`flex-1 text-sm ${item.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                          {item.title}
                        </span>
                        <ChevronRight className={`w-4 h-4 ${item.completed ? 'text-gray-300' : 'text-gray-400'}`} />
                      </button>
                    ))}
                  </div>
                  
                  {/* Progress indicator */}
                  <div className="mt-6 pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Progress</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {Object.values(onboardingStats).filter(Boolean).length}/5
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gray-900 rounded-full transition-all duration-500"
                        style={{ width: `${(Object.values(onboardingStats).filter(Boolean).length / 5) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    Mobile Tip
                  </h4>
                  <p className="text-blue-800 mt-1">
                    Add this site to your phone's home screen for quick access in the field! 
                    Open in Safari (iPhone) or Chrome (Android), tap the menu, and select "Add to Home Screen".
                  </p>
                </div>
              </div>
            )}

            {/* Customers */}
            {activeSection === 'customers' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Managing Customers</h2>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Adding a New Customer</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Click <strong>Customers</strong> in the left sidebar</li>
                    <li>Click <strong>+ New Customer</strong></li>
                    <li>Fill in their name, phone, email, and address</li>
                    <li>Click <strong>Save</strong></li>
                  </ol>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                    <p className="text-amber-800">
                      <strong>Pro Tip:</strong> Use the job site address, not their mailing address. 
                      This makes it easier to navigate to the location.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Customer Profile</h3>
                  <p className="text-gray-700">
                    Each customer has a profile page showing:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Contact information</li>
                    <li>All site visits</li>
                    <li>Estimates and their status</li>
                    <li>Jobs history</li>
                    <li>Invoices and payment status</li>
                    <li>Activity log of all interactions</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Site Visits */}
            {activeSection === 'site-visits' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Site Visits</h2>
                <p className="text-muted-foreground">
                  Site visits are where you assess the job before giving an estimate.
                </p>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Scheduling a Site Visit</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Click <strong>+ New Site Visit</strong> in the sidebar</li>
                    <li>Select the customer (or create a new one)</li>
                    <li>Choose the service type</li>
                    <li>Set the date and time</li>
                    <li>Add any initial notes about what they're looking for</li>
                    <li>Click <strong>Create Site Visit</strong></li>
                  </ol>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">At the Job Site</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Open the site visit on your phone</li>
                    <li>Tap <strong>Field Notes</strong> to document everything</li>
                    <li>Take photos, record measurements, add notes</li>
                    <li>Tap <strong>Save</strong> when done</li>
                  </ol>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">After the Visit</h3>
                  <p className="text-gray-700">
                    Once you've completed the site visit, you can:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Click <strong>Convert to Estimate</strong> to create a quote</li>
                    <li>All your field notes and photos will transfer over</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Field Notes */}
            {activeSection === 'field-notes' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Field Notes</h2>
                <p className="text-muted-foreground">
                  Document everything on-site with photos, measurements, and notes.
                </p>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">What to Capture</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <Camera className="w-6 h-6 text-primary mb-2" />
                      <h4 className="font-medium">Photos</h4>
                      <p className="text-sm text-gray-600">Before, during, and after shots. Tag them for easy reference.</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <FileText className="w-6 h-6 text-primary mb-2" />
                      <h4 className="font-medium">Measurements</h4>
                      <p className="text-sm text-gray-600">Square footage, dimensions, quantities needed.</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <MessageSquare className="w-6 h-6 text-primary mb-2" />
                      <h4 className="font-medium">Notes</h4>
                      <p className="text-sm text-gray-600">Client requests, site conditions, access issues.</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-primary mb-2" />
                      <h4 className="font-medium">Checklist</h4>
                      <p className="text-sm text-gray-600">Make sure you've covered everything before leaving.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900">Photo Tips</h4>
                  <ul className="text-green-800 mt-2 space-y-1">
                    <li>• <strong>Before:</strong> Capture existing damage, cracks, current condition</li>
                    <li>• <strong>During:</strong> Document the work process</li>
                    <li>• <strong>After:</strong> Show finished work from multiple angles</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Estimates */}
            {activeSection === 'estimates' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Creating Estimates</h2>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Creating an Estimate</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Go to a completed site visit</li>
                    <li>Click <strong>Convert to Estimate</strong></li>
                    <li>Add line items with quantities and prices</li>
                    <li>Review the total</li>
                    <li>Click <strong>Save Estimate</strong></li>
                  </ol>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Sending to Customer</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Open the estimate</li>
                    <li>Click <strong>Send to Customer</strong></li>
                    <li>They'll receive an email with a professional PDF</li>
                    <li>They can view online and approve with one click</li>
                    <li>You'll be notified when they approve!</li>
                  </ol>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">After Approval</h3>
                  <p className="text-gray-700">
                    Once approved, click <strong>Convert to Job</strong> to schedule the work.
                  </p>
                </div>
              </div>
            )}

            {/* Jobs */}
            {activeSection === 'jobs' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Jobs & Calendar</h2>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Creating a Job</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Go to an approved estimate</li>
                    <li>Click <strong>Convert to Job</strong></li>
                    <li>Set the start and end dates</li>
                    <li>Assign crew members (optional)</li>
                    <li>Click <strong>Create Job</strong></li>
                  </ol>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Calendar View</h3>
                  <p className="text-gray-700">
                    The Jobs page shows your calendar with all scheduled work. You can:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>View by week or list</li>
                    <li>See site visits (single day) and jobs (full duration)</li>
                    <li>Click any item to see details</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Job Photos</h3>
                  <p className="text-gray-700">
                    Add before/after photos to each job for documentation and marketing:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Tag photos as Before, After, Progress, or Damage</li>
                    <li>Use Compare view to see side-by-side</li>
                    <li>Export all photos for your portfolio</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Invoices */}
            {activeSection === 'invoices' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Invoices</h2>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Creating an Invoice</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Go to a completed job</li>
                    <li>Click <strong>Create Invoice</strong></li>
                    <li>Review the amounts (pulled from estimate)</li>
                    <li>Click <strong>Send Invoice</strong></li>
                  </ol>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Customer Payment</h3>
                  <p className="text-gray-700">
                    The invoice shows your payment options (from Settings → Payments). 
                    Customer pays via their preferred method, then you mark it paid in the system.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Recording Payment</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Open the invoice</li>
                    <li>Click <strong>Record Payment</strong></li>
                    <li>Enter amount and payment method</li>
                    <li>Save</li>
                  </ol>
                </div>
              </div>
            )}

            {/* Payment Setup */}
            {activeSection === 'payments' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Payment Setup</h2>
                <p className="text-muted-foreground">
                  Configure how your customers can pay you. These options appear on invoices.
                </p>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Setting Up Payment Methods</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Go to <strong>Settings</strong> → <strong>Payments</strong> tab</li>
                    <li>Toggle ON each method you accept</li>
                    <li>Fill in your payment links/handles:
                      <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                        <li>Venmo: Your @handle</li>
                        <li>Cash App: Your $cashtag</li>
                        <li>PayPal: Your PayPal.me link</li>
                        <li>Zelle: Your email or phone</li>
                        <li>Check: Who to make it payable to</li>
                      </ul>
                    </li>
                    <li>Add any payment instructions</li>
                    <li>Click <strong>Save Payment Settings</strong></li>
                  </ol>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900">Recommended Setup</h4>
                  <ul className="text-blue-800 mt-2 space-y-1">
                    <li>• <strong>Check</strong> - Best for large jobs</li>
                    <li>• <strong>Zelle</strong> - Fast, no fees</li>
                    <li>• <strong>Card</strong> - Convenient for deposits</li>
                    <li>• <strong>Cash</strong> - For final payments on-site</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Industry-Specific Tips */}
            {activeSection === 'industry-tips' && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <industryData.icon className="w-7 h-7 text-primary" />
                  <h2 className="font-heading text-2xl font-bold">{industryData.name} Tips</h2>
                </div>
                <p className="text-muted-foreground">
                  Industry-specific guidance for {industryData.name.toLowerCase()} professionals.
                </p>
                
                {/* Photo Tips */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Camera className="w-5 h-5 text-primary" />
                    Photo Documentation Tips
                  </h3>
                  <div className="grid gap-3">
                    {industryData.photoTips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <Badge className={tip.type === 'Before' ? 'bg-blue-100 text-blue-700' : tip.type === 'After' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {tip.type}
                        </Badge>
                        <span className="text-gray-700">{tip.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Measurement Tips */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Measurement Guide
                  </h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <ul className="space-y-2">
                      {industryData.measurementTips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Common Line Items */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary" />
                    Common Estimate Line Items
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Copy these into your estimates as needed:
                  </p>
                  <div className="bg-white border rounded-lg divide-y">
                    {industryData.commonLineItems.map((item, i) => (
                      <div key={i} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        {item}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    💡 Tip: Adjust pricing for your market and job complexity.
                  </p>
                </div>
              </div>
            )}

            {/* Mobile Tips */}
            {activeSection === 'mobile' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Mobile Tips</h2>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Add to Home Screen</h3>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-2">iPhone (Safari)</h4>
                      <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                        <li>Open this site in Safari</li>
                        <li>Tap the Share button</li>
                        <li>Scroll down, tap "Add to Home Screen"</li>
                        <li>Name it and tap Add</li>
                      </ol>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-2">Android (Chrome)</h4>
                      <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                        <li>Open this site in Chrome</li>
                        <li>Tap the 3-dot menu</li>
                        <li>Tap "Add to Home Screen"</li>
                        <li>Name it and tap Add</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Quick Access Features</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Swipe to close</strong> - Swipe right on Field Notes to quickly close</li>
                    <li><strong>Save button in header</strong> - Always visible on mobile</li>
                    <li><strong>Tab icons</strong> - Compact navigation on small screens</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Offline Mode */}
            {activeSection === 'offline' && (
              <div className="space-y-6">
                <h2 className="font-heading text-2xl font-bold">Offline Mode</h2>
                <p className="text-muted-foreground">
                  Work in areas with poor signal? No problem - your data is safe.
                </p>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">How It Works</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Field notes save to your phone automatically</li>
                    <li>When you're back online, everything syncs</li>
                    <li>Look for <strong>"Pending Sync"</strong> badge if data hasn't uploaded yet</li>
                  </ul>
                </div>

                <div className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <WifiOff className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-amber-900">Working Offline</h4>
                    <p className="text-amber-800 mt-1">
                      If you see "Pending Sync" on the Field Notes button, your notes are saved locally 
                      but haven't synced to the server yet. They'll upload automatically when you have signal.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <Wifi className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-green-900">Back Online</h4>
                    <p className="text-green-800 mt-1">
                      Once you're back online, your data syncs automatically. You'll see a success 
                      message when everything is uploaded.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Need More Help? */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 text-center">
          <HelpCircle className="w-12 h-12 mx-auto text-primary mb-4" />
          <h3 className="font-heading text-xl font-semibold mb-2">Need More Help?</h3>
          <p className="text-muted-foreground mb-4">
            We're here to help you succeed. Reach out anytime!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="outline">
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpGuidePage;
